import { createContext, useContext, ReactNode, useMemo } from 'react';
import type { ModuleId } from './ModuleContext';

/**
 * FormConsumerContext — Decouples form consumer pages from hardcoded /forms/* paths.
 *
 * When form pages are mounted under a host module (e.g., /field-service/reports),
 * the context provides the correct navigation paths for that module.
 *
 * Default basePath is '/forms' for backwards compatibility when used within P8.
 */

interface FormConsumerPaths {
  /** Base path for all form consumer routes (e.g., '/forms', '/field-service/reports') */
  basePath: string;
  /** Path to submissions list */
  submissions: string;
  /** Path to a specific submission detail */
  submissionDetail: (id: string) => string;
  /** Path to edit a submission */
  submissionEdit: (id: string) => string;
  /** Path to view a submission report */
  submissionReport: (id: string) => string;
  /** Path to fill a form template */
  fill: (templateId: string, draftId?: string) => string;
  /** Path to template list (only available in P8) */
  templates: string;
  /** Label for the module context (e.g., 'Forms', 'Service Reports', 'Inspections') */
  moduleLabel: string;
  /** Module ID for template filtering (e.g., 'P11' for Field Service) */
  moduleId?: ModuleId;
}

const DEFAULT_BASE = '/forms';

function buildPaths(basePath: string, moduleLabel: string, moduleId?: ModuleId): FormConsumerPaths {
  return {
    basePath,
    submissions: `${basePath}/submissions`,
    submissionDetail: (id: string) => `${basePath}/submissions/${id}`,
    submissionEdit: (id: string) => `${basePath}/submissions/${id}/edit`,
    submissionReport: (id: string) => `${basePath}/submissions/${id}/report`,
    fill: (templateId: string, draftId?: string) => {
      const base = `${basePath}/fill/${templateId}`;
      return draftId ? `${base}?draftId=${draftId}` : base;
    },
    templates: `${basePath}/templates`,
    moduleLabel,
    moduleId,
  };
}

const FormConsumerContext = createContext<FormConsumerPaths>(
  buildPaths(DEFAULT_BASE, 'Forms', 'P8')
);

interface FormConsumerProviderProps {
  children: ReactNode;
  /** Base path for routes (e.g., '/field-service/reports') */
  basePath: string;
  /** Display label for this context (e.g., 'Service Reports') */
  moduleLabel?: string;
  /** Module ID for template filtering — only templates targeting this module will appear */
  moduleId?: ModuleId;
}

export function FormConsumerProvider({
  children,
  basePath,
  moduleLabel = 'Forms',
  moduleId,
}: FormConsumerProviderProps) {
  const paths = useMemo(() => buildPaths(basePath, moduleLabel, moduleId), [basePath, moduleLabel, moduleId]);

  return (
    <FormConsumerContext.Provider value={paths}>
      {children}
    </FormConsumerContext.Provider>
  );
}

/**
 * Hook to get form consumer navigation paths.
 * Returns default /forms/* paths when used outside a FormConsumerProvider.
 */
export function useFormConsumerPaths(): FormConsumerPaths {
  return useContext(FormConsumerContext);
}
